import { useForm } from "@mittwald/flow-components/dist/components/Form";
import LoadingView from "@mittwald/flow-components/dist/components/LoadingView";
import { Section } from "@mittwald/flow-components/dist/components/Section";
import { Text } from "@mittwald/flow-components/dist/components/Text";
import { TextLink } from "@mittwald/flow-components/dist/components/TextLink";
import { TranslationProvider } from "@mittwald/flow-components/dist/components/TranslationProvider";
import { WizardStep } from "@mittwald/flow-components/dist/components/Wizard/components/WizardStep";
import type { WizardState } from "@mittwald/flow-components/dist/components/Wizard/state/model";
import type { AnimationController } from "@mittwald/flow-components/dist/hooks/useAnimationController";
import Suspense from "@mittwald/flow-lib/dist/resources/Suspense";
import type { FC } from "react";
import React, { useState } from "react";
import { CodeInput } from "../../../../components/CodeInput";
import { useCodeInputAutoSubmit } from "../../../../hooks/useCodeInputAutoSubmit";
import type { VerifyMfaInputs } from "../../../../model/signup/Signup";
import { Signup } from "../../../../model/signup/Signup";
import VirtualPageViewTracking from "../../../components/tracking/components/VirtualPageViewTracking";

export interface MfaStepContentProps {
  rejectionAnimation: AnimationController;
  trackingId?: string;
  email: string;
  password: string;
  wizardState: WizardState;
}

export const MfaStep: FC<MfaStepContentProps> = (props) => {
  const [recovery, setRecovery] = useState(false);
  const { rejectionAnimation, trackingId, password, email, wizardState } =
    props;

  const mfaForm = useForm<VerifyMfaInputs>({
    onSubmit: async (values) => {
      await Signup.verifyMfa(values, email, password, rejectionAnimation);
      wizardState.gotoNextStep();
    },
  });

  useCodeInputAutoSubmit(mfaForm, "multiFactorCode", recovery ? 16 : 6, {
    reset: !recovery,
    autoSubmit: !recovery,
  });

  const recoveryInput = (
    <>
      <CodeInput
        label="input"
        maxLength={16}
        rejectionAnimation={rejectionAnimation}
      />
    </>
  );

  const toggleInputMode = (): void => {
    setRecovery((isRecovery) => !isRecovery);
  };

  return (
    <TranslationProvider
      name={recovery ? "recoveryStepContent" : "mfaStepContent"}
      type="component"
    >
      <WizardStep form={mfaForm} id="mfa" indicatorText="mfa" noReturn>
        <Suspense loadingView={<LoadingView _height={250} />}>
          {trackingId && (
            <VirtualPageViewTracking
              identifier={trackingId}
              name="checkout/login/mfa"
            />
          )}
          <Section.Item headline="mfa">
            <Text i18n="mfaText" />
            <TextLink action={toggleInputMode} i18n="toggle" />
            {recovery && recoveryInput}
            {!recovery && (
              <CodeInput
                label="2fa"
                maxLength={6}
                preventEnterPress
                rejectionAnimation={rejectionAnimation}
              />
            )}
          </Section.Item>
        </Suspense>
      </WizardStep>
    </TranslationProvider>
  );
};
